// Activo Fijo → Activos → Componentes (componetización NIC 16, registro).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Component, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { ActivoRow, Campo, Componente, METODOS, Modal, Switch, afRpc, fieldCls, money } from './shared';

const vacio = { id: '', nombre: '', costo: 0, valor_residual: 0, vida_util_meses: 0, metodo: 'linea_recta', fecha_inicio_depreciacion: '', es_activo: true };

export function ComponentesAF() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: activos = [] } = useQuery<ActivoRow[]>({ queryKey: ['af-activos', 'activo', ''], queryFn: () => afRpc<ActivoRow[]>('af_activos', { _estado: 'activo', _busq: null }) });
  const [activoId, setActivoId] = useState('');
  const { data: comps = [], isFetching } = useQuery<Componente[]>({ queryKey: ['af-comps', activoId], queryFn: () => afRpc<Componente[]>('af_componentes', { _activo_id: activoId }), enabled: !!activoId });
  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const set = (p: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...p }));

  const abrir = (c?: Componente) => {
    if (c) setForm({ id: c.id, nombre: c.nombre, costo: Number(c.costo), valor_residual: Number(c.valor_residual), vida_util_meses: c.vida_util_meses ?? 0, metodo: c.metodo, fecha_inicio_depreciacion: c.fecha_inicio_depreciacion ?? '', es_activo: c.es_activo });
    else setForm(vacio);
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => afRpc('af_componente_guardar', { _data: { id: form.id || undefined, activo_id: activoId, nombre: form.nombre.trim(), costo: Number(form.costo) || 0, valor_residual: Number(form.valor_residual) || 0, vida_util_meses: Number(form.vida_util_meses) || null, metodo: form.metodo, fecha_inicio_depreciacion: form.fecha_inicio_depreciacion || null, es_activo: form.es_activo } }),
    onSuccess: () => { toast.success('Componente guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['af-comps', activoId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => afRpc('af_componente_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Componente eliminado'); void qc.invalidateQueries({ queryKey: ['af-comps', activoId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><Component className="w-5 h-5" /> Componentes</h1>
      <p className="text-sm text-muted-foreground">Desglose de un activo en componentes con vida útil propia (NIC 16). Registro informativo; para depreciar por separado, registra cada parte como un activo independiente.</p>
      <div className="flex items-end gap-2">
        <Campo label="Activo" className="flex-1 max-w-lg">
          <select className={fieldCls} value={activoId} onChange={(e) => setActivoId(e.target.value)}>
            <option value="">— Selecciona un activo —</option>
            {activos.map((a) => <option key={a.id} value={a.id}>{a.placa} · {a.nombre}</option>)}
          </select>
        </Campo>
        <Button disabled={!activoId} onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Componente</Button>
      </div>

      {activoId && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Componentes ({comps.length})</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Componente</th><th className="py-2 pr-3 text-right">Costo</th><th className="py-2 pr-3 text-right">Vida (meses)</th><th className="py-2 pr-3">Método</th><th className="py-2"></th></tr></thead>
              <tbody>
                {comps.length === 0 && !isFetching && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin componentes</td></tr>}
                {comps.map((c) => (
                  <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 font-medium">{c.nombre}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.costo)}</td>
                    <td className="py-2 pr-3 text-right">{c.vida_util_meses ?? '—'}</td>
                    <td className="py-2 pr-3">{METODOS.find((m) => m.v === c.metodo)?.l ?? c.metodo}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${c.nombre}"?`, { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {abierto && (
        <Modal title={form.id ? 'Editar componente' : 'Nuevo componente'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Costo"><input type="number" step="0.01" min="0" className={fieldCls} value={form.costo} onChange={(e) => set({ costo: Number(e.target.value) })} /></Campo>
              <Campo label="Valor residual"><input type="number" step="0.01" min="0" className={fieldCls} value={form.valor_residual} onChange={(e) => set({ valor_residual: Number(e.target.value) })} /></Campo>
              <Campo label="Vida útil (meses)"><input type="number" min="0" className={fieldCls} value={form.vida_util_meses} onChange={(e) => set({ vida_util_meses: Number(e.target.value) })} /></Campo>
              <Campo label="Método"><select className={fieldCls} value={form.metodo} onChange={(e) => set({ metodo: e.target.value })}>{METODOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}</select></Campo>
            </div>
            <Campo label="Fecha de puesta en servicio"><input type="date" className={fieldCls} value={form.fecha_inicio_depreciacion} onChange={(e) => set({ fecha_inicio_depreciacion: e.target.value })} /></Campo>
            <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
